import { addDays, diffDays, startOfWeek, isoWeek } from './constants';
import { MONTH_LABELS } from '../utils/helpers';

// Unidades del encabezado del Gantt por zoom. Cada unidad es un tramo de días
// { key, label, labelCorto, offset, width } medido en días desde firstDay,
// recortado a la ventana. Se usa también para las líneas verticales del
// cuerpo, así grilla y encabezado coinciden.
//
//   Día       -> arriba meses,  abajo días
//   Semana    -> arriba meses,  abajo semanas
//   Mes       -> arriba años,   abajo meses
//   Trimestre -> arriba años,   abajo trimestres

const mesCorto = (m) => MONTH_LABELS[m].slice(0, 3).toLowerCase();

// Recorta [inicio, finExclusivo) a la ventana y lo pasa a offsets en días.
const tramo = (inicio, finExclusivo, firstDay, totalDays) => {
  const a = Math.max(0, diffDays(inicio, firstDay));
  const b = Math.min(totalDays, diffDays(finExclusivo, firstDay));
  return b > a ? { offset: a, width: b - a } : null;
};

const meses = (firstDay, totalDays) => {
  const out = [];
  let cur = new Date(firstDay.getFullYear(), firstDay.getMonth(), 1);
  const fin = addDays(firstDay, totalDays);
  while (cur < fin) {
    const sig = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    const t = tramo(cur, sig, firstDay, totalDays);
    if (t) {
      out.push({
        key: `m-${cur.getFullYear()}-${cur.getMonth()}`,
        label: `${MONTH_LABELS[cur.getMonth()]} ${cur.getFullYear()}`,
        labelMedio: MONTH_LABELS[cur.getMonth()],
        labelCorto: mesCorto(cur.getMonth()),
        fecha: cur,
        ...t,
      });
    }
    cur = sig;
  }
  return out;
};

const anios = (firstDay, totalDays) => {
  const out = [];
  let cur = new Date(firstDay.getFullYear(), 0, 1);
  const fin = addDays(firstDay, totalDays);
  while (cur < fin) {
    const sig = new Date(cur.getFullYear() + 1, 0, 1);
    const t = tramo(cur, sig, firstDay, totalDays);
    if (t) out.push({ key: `y-${cur.getFullYear()}`, label: String(cur.getFullYear()), labelCorto: String(cur.getFullYear()), fecha: cur, ...t });
    cur = sig;
  }
  return out;
};

const trimestres = (firstDay, totalDays) => {
  const out = [];
  let cur = new Date(firstDay.getFullYear(), Math.floor(firstDay.getMonth() / 3) * 3, 1);
  const fin = addDays(firstDay, totalDays);
  while (cur < fin) {
    const sig = new Date(cur.getFullYear(), cur.getMonth() + 3, 1);
    const t = tramo(cur, sig, firstDay, totalDays);
    if (t) {
      const q = Math.floor(cur.getMonth() / 3) + 1;
      out.push({
        key: `q-${cur.getFullYear()}-${q}`,
        label: `T${q}`,
        detalle: `${mesCorto(cur.getMonth())} – ${mesCorto(cur.getMonth() + 2)}`,
        labelCorto: `T${q}`,
        fecha: cur,
        ...t,
      });
    }
    cur = sig;
  }
  return out;
};

const semanas = (firstDay, totalDays) => {
  const out = [];
  let cur = startOfWeek(firstDay);
  const fin = addDays(firstDay, totalDays);
  while (cur < fin) {
    const sig = addDays(cur, 7);
    const t = tramo(cur, sig, firstDay, totalDays);
    if (t) {
      const domingo = addDays(cur, 6);
      out.push({
        key: `w-${cur.toISOString()}`,
        label: `S${isoWeek(cur)}`,
        detalle: cur.getMonth() === domingo.getMonth()
          ? `${cur.getDate()}–${domingo.getDate()}`
          : `${cur.getDate()} ${mesCorto(cur.getMonth())} – ${domingo.getDate()} ${mesCorto(domingo.getMonth())}`,
        fecha: cur,
        ...t,
      });
    }
    cur = sig;
  }
  return out;
};

const dias = (firstDay, totalDays) => Array.from({ length: totalDays }, (_, i) => {
  const d = addDays(firstDay, i);
  return { key: `d-${i}`, fecha: d, offset: i, width: 1 };
});

export const escalaGantt = (zoom, firstDay, totalDays) => {
  switch (zoom) {
    case 'dia':
      return { arriba: meses(firstDay, totalDays), abajo: dias(firstDay, totalDays), tipoAbajo: 'dia' };
    case 'semana':
      return { arriba: meses(firstDay, totalDays), abajo: semanas(firstDay, totalDays), tipoAbajo: 'semana' };
    case 'mes':
      return { arriba: anios(firstDay, totalDays), abajo: meses(firstDay, totalDays), tipoAbajo: 'mes' };
    case 'trimestre':
    default:
      return { arriba: anios(firstDay, totalDays), abajo: trimestres(firstDay, totalDays), tipoAbajo: 'trimestre' };
  }
};
